import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { Info } from 'lucide-react'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { ProjectBlockedState } from '@/features/projects/components/ProjectBlockedState'
import { useProjectVersions } from '@/features/versions/hooks/useProjectVersions'
import { asApiError } from '@/services/apiError'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  openRequirements,
  requirementsClosed,
  requirementsErrorDismissed,
  requirementsRefetchArmed,
  selectRequirementRowLoaded,
  selectRequirementRows,
  selectRequirementsBlocked,
  selectRequirementsError,
  selectRequirementsLoading,
  selectRequirementsNonce
} from '../slices/requirementsSlice'
import type {
  RequirementFilters,
  RequirementFitGapVerdict,
  RequirementRow,
  RequirementStatus,
  RequirementTraceRef
} from '../types'
import { BulkImportPanel } from './BulkImportPanel'
import { FitGapSummary } from './FitGapSummary'
import { RequirementActions } from './RequirementActions'

const STATUS_TONE: Record<RequirementStatus, StatusTone> = {
  PROPOSED: 'proposed',
  CONFIRMED: 'done',
  REJECTED: 'blocked',
  SUPERSEDED: 'neutral'
}

const VERDICT_TONE: Record<RequirementFitGapVerdict, StatusTone> = {
  STANDARD: 'done',
  LIBRARY: 'proposed',
  NEW: 'neutral',
  UNCLASSIFIED: 'review'
}

const STATUS_CHIPS: readonly RequirementStatus[] = ['PROPOSED', 'CONFIRMED', 'REJECTED', 'SUPERSEDED']
const VERDICT_CHIPS: readonly RequirementFitGapVerdict[] = ['STANDARD', 'LIBRARY', 'NEW', 'UNCLASSIFIED']

/** One filter chip's classes — the single visual definition the status, verdict and version rows share. */
const chipClass = (active: boolean): string =>
  cn(
    'rounded-full border px-3 py-1 text-[13px] transition-colors',
    active
      ? 'border-mint bg-accent font-semibold text-accent-foreground'
      : 'border-border bg-card text-ink-soft hover:bg-muted'
  )

/**
 * AC-13 — traceability degrades honestly. `INTAKE_TURN` /
 * `ELEMENT_CONCLUSION` / `MEETING_EVIDENCE` link into the Interviews page for
 * this project via a plain `react-router` `<Link>` built off the route
 * param — this module imports NOTHING from `features/intakes`, so it is
 * correct whether or not that route has landed (Decision 10). `MANUAL`
 * renders its note as text, with no link.
 */
function TraceRefEntry({
  reference,
  projectId
}: {
  reference: RequirementTraceRef
  projectId: string
}) {
  const { t } = useTranslation('projectWorkspace')
  const label = reference.kind === 'MANUAL' && reference.note !== undefined
    ? reference.note
    : t(`requirements.traceKind.${reference.kind}`)
  if (reference.kind === 'MANUAL') {
    return <span className="text-[12px] text-ink-soft">{label}</span>
  }
  return (
    <Link to={`/project/${projectId}/interviews`} className="text-[12px] text-accent-foreground hover:underline">
      {label}
    </Link>
  )
}

/**
 * The register's data-owning panel (PX-W7.2, PX-W6.3 idiom): owns the
 * `openRequirements` mount/refetch effect, the server-side filter chips
 * (AC-2 — every chip is a query parameter, a cleared chip sends none), the
 * three data states (AC-8), the blocked surface (AC-9), and composes the
 * derived fit-gap summary and the bulk-import trigger.
 */
export function RequirementsRegisterPanel({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const rows = useAppSelector(selectRequirementRows)
  const loading = useAppSelector(selectRequirementsLoading)
  const error = useAppSelector(selectRequirementsError)
  const blocked = useAppSelector(selectRequirementsBlocked)
  const nonce = useAppSelector(selectRequirementsNonce)
  // TASK-3D6DB8B6 fix cycle 1: ONLY this project's versions (empty until its
  // own read lands) — never project A's chips on project B.
  const versions = useProjectVersions(orgId, projectId)

  const [status, setStatus] = useState<RequirementStatus | null>(null)
  const [verdict, setVerdict] = useState<RequirementFitGapVerdict | null>(null)
  // The version chip pick is keyed by project, so A's versionId can never
  // filter B's register after an in-app A→B switch (the panel stays mounted).
  const [versionPick, setVersionPick] = useState<{ projectId: string; versionId: string } | null>(null)
  const versionId = versionPick !== null && versionPick.projectId === projectId ? versionPick.versionId : null
  const setVersionId = (id: string | null): void =>
    setVersionPick(id === null ? null : { projectId, versionId: id })
  const [elementKeyDraft, setElementKeyDraft] = useState('')
  const [ownerIdDraft, setOwnerIdDraft] = useState('')
  const [advanced, setAdvanced] = useState<{ elementKey?: string; ownerId?: string }>({})
  // CR-BUILD-BOARD-EPICS-2026-09-26 (AC-20): a task card's requirement chip links
  // here with `?ref=`; that row is marked current and scrolled to ONCE per ref.
  const [searchParams] = useSearchParams()
  const highlightRef = searchParams.get('ref')
  const refRowLoaded = useAppSelector((s) => selectRequirementRowLoaded(s, highlightRef))
  const scrolledToRef = useRef<string | null>(null)

  const filters: RequirementFilters = useMemo(
    () => ({
      status: status ?? undefined,
      verdict: verdict ?? undefined,
      versionId: versionId ?? undefined,
      elementKey: advanced.elementKey,
      ownerId: advanced.ownerId
    }),
    [status, verdict, versionId, advanced]
  )
  const filtersKey = JSON.stringify(filters)

  // AC-2 (finding 2, fix wave 1): the version chip reuses the already-live
  // `features/versions` slice through `useProjectVersions` — BulkImportPanel
  // (rendered below) loads it too, but `fetchVersions`'s keyed cache
  // condition makes the second dispatch a no-op, so this never duplicates
  // the request.

  // Fix cycle 3 (GRP-B04710BD, px-effect-cleanup-on-nonce-dep-resets-the-slice):
  // the reset is its OWN effect, keyed only on identity (`orgId`/`projectId`),
  // so its cleanup fires on unmount and on a project change — never on a
  // `filtersKey` or realtime `nonce` change. Folding it into the fetch effect
  // below used to mean React ran this cleanup (which wipes `bulkImport`, the
  // bulk-import counts, along with everything else) on EVERY dependency
  // change, not only at unmount — so a chip change or a remote realtime frame
  // silently erased the counts `BulkImportPanel` had just reported.
  useEffect(() => {
    return () => {
      dispatch(requirementsClosed())
    }
  }, [dispatch, orgId, projectId])

  // The mount/refetch effect — no cleanup. A filter change or a nonce bump
  // only needs fresh data; `openRequirements`'s own `shouldFetch` condition
  // (keyed by project + the exact filter set, `cacheKeyOf`) turns either one
  // into exactly the refetch it should be, never a duplicate.
  useEffect(() => {
    if (orgId === '' || projectId === '') return
    void dispatch(openRequirements({ orgId, projectId, filters: JSON.parse(filtersKey) as RequirementFilters }))
    // filtersKey (not `filters`) is the dependency: the object literal is
    // recreated every render, so depending on it directly would refetch on
    // every render regardless of content — filtersKey is its stable,
    // content-equal proxy.
  }, [dispatch, orgId, projectId, filtersKey, nonce])

  useEffect(() => {
    if (highlightRef === null || !refRowLoaded || scrolledToRef.current === highlightRef) return
    const element = document.getElementById(`requirement-${highlightRef}`)
    if (element !== null && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'center' })
      scrolledToRef.current = highlightRef
    }
  }, [highlightRef, refRowLoaded])

  const onAdvancedApplyClicked = (): void => {
    setAdvanced({
      elementKey: elementKeyDraft.trim() || undefined,
      ownerId: ownerIdDraft.trim() || undefined
    })
  }
  // Deferred finding c1-2 (TASK-71426B1C): `requirementsErrorDismissed` alone
  // does not invalidate the fetch cache, so `openRequirements`'s own
  // `shouldFetch` condition (loadedKey unchanged, `invalidated: false` after
  // a failed fetch) skipped this dispatch and Retry was a silent no-op.
  // `requirementsRefetchArmed()` re-arms the cache before the load.
  const onRetryClicked = (): void => {
    dispatch(requirementsErrorDismissed())
    dispatch(requirementsRefetchArmed())
    void dispatch(openRequirements({ orgId, projectId, filters }))
  }

  // Finding 1 (fix wave 1, GRP-B3D9F416): `error` carries the unscoped
  // list-read failure AND every scoped refusal — a row-scoped verdict
  // refusal keyed by the acting `ref` (renders inline on its own row via
  // `RequirementActions`), and, since deferred finding c2-1, the
  // `'bulk-import'`-scoped channel (renders inline in `BulkImportPanel`).
  // Only the unscoped one is this panel's business (AC-3/AC-5: the refusal
  // is inline, the row byte-identical, no other row disturbed). Branching
  // the whole table on `error !== null` unmounted the register on every
  // scoped refusal; branch on `listError` instead.
  const listError = error !== null && error.scope === undefined ? error : null

  if (blocked !== null) {
    return (
      <ProjectBlockedState
        blocked={{ kind: 'blocked', status: blocked.status, code: blocked.code, message: blocked.message }}
      />
    )
  }

  return (
    <div>
      <section
        className="flex flex-wrap items-end justify-between gap-3"
        aria-label={t('requirements.filters.legend')}
      >
        <div>
          <SectionLabel>{t('requirements.filters.statusLabel')}</SectionLabel>
          <div className="mt-1.5 flex flex-wrap gap-1.5" role="group" aria-label={t('requirements.filters.statusLegend')}>
            <button
              type="button"
              aria-pressed={status === null}
              onClick={() => setStatus(null)}
              className={chipClass(status === null)}
            >
              {t('requirements.filters.all')}
            </button>
            {STATUS_CHIPS.map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={status === value}
                onClick={() => setStatus(value)}
                className={chipClass(status === value)}
              >
                {t(`requirements.status.${value}`)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <SectionLabel>{t('requirements.filters.verdictLabel')}</SectionLabel>
          <div className="mt-1.5 flex flex-wrap gap-1.5" role="group" aria-label={t('requirements.filters.verdictLegend')}>
            <button
              type="button"
              aria-pressed={verdict === null}
              onClick={() => setVerdict(null)}
              className={chipClass(verdict === null)}
            >
              {t('requirements.filters.all')}
            </button>
            {VERDICT_CHIPS.map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={verdict === value}
                onClick={() => setVerdict(value)}
                className={chipClass(verdict === value)}
              >
                {t(`requirements.verdict.${value}`)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <SectionLabel>{t('requirements.filters.versionLabel')}</SectionLabel>
          <div className="mt-1.5 flex flex-wrap gap-1.5" role="group" aria-label={t('requirements.filters.versionLegend')}>
            <button
              type="button"
              aria-pressed={versionId === null}
              onClick={() => setVersionId(null)}
              className={chipClass(versionId === null)}
            >
              {t('requirements.filters.all')}
            </button>
            {versions.map((version) => (
              <button
                key={version.id}
                type="button"
                aria-pressed={versionId === version.id}
                onClick={() => setVersionId(version.id)}
                data-testid={`version-chip-${version.id}`}
                className={chipClass(versionId === version.id)}
              >
                {version.name}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-foreground">{t('requirements.filters.elementKeyLabel')}</span>
            <input
              value={elementKeyDraft}
              onChange={(event) => setElementKeyDraft(event.target.value)}
              className="h-8 rounded-md border border-input bg-card px-2 text-[13px] text-foreground"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-foreground">{t('requirements.filters.ownerIdLabel')}</span>
            <input
              value={ownerIdDraft}
              onChange={(event) => setOwnerIdDraft(event.target.value)}
              className="h-8 rounded-md border border-input bg-card px-2 text-[13px] text-foreground"
            />
          </label>
          <button
            type="button"
            onClick={onAdvancedApplyClicked}
            className="h-8 rounded-md border border-input bg-card px-3 text-[13px] font-medium text-foreground hover:bg-muted"
          >
            {t('requirements.filters.apply')}
          </button>
        </div>
      </section>

      <section
        id="requirements-register"
        aria-labelledby="register-heading"
        className="mt-4 rounded-lg border border-border bg-card"
      >
        <div className="border-b border-border px-4 py-3">
          <SectionLabel>{t('requirements.table.label')}</SectionLabel>
          <h2 id="register-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
            {t('requirements.table.heading')}
          </h2>
        </div>

        {highlightRef !== null && rows.length > 0 && !refRowLoaded && (
          <p
            data-testid="requirement-ref-missing"
            className="mx-4 mt-3 flex items-start gap-2 rounded-md border border-dashed border-border px-3 py-2 text-[0.8125rem] text-ink-soft"
          >
            <Info aria-hidden className="mt-0.5 size-4 shrink-0" />
            {t('requirements.refNotInView', { ref: highlightRef })}
          </p>
        )}

        {loading && rows.length === 0 ? (
          <LoadingPanel label={t('requirements.loadingLabel')} />
        ) : listError !== null ? (
          <ErrorPanel
            error={asApiError({ status: listError.status, key: listError.code, message: listError.message })}
            onRetryClicked={onRetryClicked}
          />
        ) : rows.length === 0 ? (
          <EmptyPanel title={t('requirements.empty.title')} body={t('requirements.empty.body')} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">{t('requirements.table.columns.ref')}</TableHead>
                <TableHead>{t('requirements.table.columns.title')}</TableHead>
                <TableHead>{t('requirements.table.columns.verdict')}</TableHead>
                <TableHead>{t('requirements.table.columns.owner')}</TableHead>
                <TableHead className="text-right">{t('requirements.table.columns.effort')}</TableHead>
                <TableHead>{t('requirements.table.columns.status')}</TableHead>
                <TableHead>{t('requirements.table.columns.source')}</TableHead>
                <TableHead>{t('requirements.table.columns.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row: RequirementRow) => (
                <TableRow
                  key={row.ref}
                  id={`requirement-${row.ref}`}
                  aria-current={row.ref === highlightRef ? 'true' : undefined}
                  data-testid={`requirement-row-${row.ref}`}
                >
                  <TableCell
                    className={cn(
                      'px-4 font-mono text-[13px] text-foreground',
                      row.ref === highlightRef && 'font-semibold shadow-[inset_0.25rem_0_0_var(--color-primary)]'
                    )}
                  >
                    {row.ref}
                  </TableCell>
                  <TableCell className="max-w-md whitespace-normal">
                    <span className="text-[13px] text-foreground">{row.title}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {row.elementKey}
                      {row.moduleKey !== undefined && ` · ${row.moduleKey}`}
                    </span>
                  </TableCell>
                  <TableCell>
                    <StatusPill tone={VERDICT_TONE[row.fitGapVerdict]}>
                      {t(`requirements.verdict.${row.fitGapVerdict}`, { defaultValue: row.fitGapVerdict })}
                    </StatusPill>
                  </TableCell>
                  <TableCell className="text-[13px] text-ink-soft">
                    {row.ownerId ?? t('requirements.table.noOwner')}
                  </TableCell>
                  <TableCell className="v-figure text-right tabular-nums text-[13px] text-ink-soft">
                    {row.effortHours === undefined
                      ? t('requirements.effort.notDisclosed')
                      : t('requirements.effort.hours', { hours: row.effortHours })}
                  </TableCell>
                  <TableCell className="px-4">
                    <div className="flex flex-col items-start gap-1">
                      <StatusPill tone={STATUS_TONE[row.status]}>
                        {t(`requirements.status.${row.status}`, { defaultValue: row.status })}
                      </StatusPill>
                      <span className="text-[11px] text-muted-foreground">
                        {t(`requirements.origin.${row.origin}`, { defaultValue: row.origin })}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      {row.refs.map((reference, index) => (
                        <TraceRefEntry key={`${row.ref}-${index}`} reference={reference} projectId={projectId} />
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <RequirementActions orgId={orgId} projectId={projectId} row={row} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <FitGapSummary />
        <BulkImportPanel orgId={orgId} projectId={projectId} />
      </div>
    </div>
  )
}
